import React, { useRef, useState } from "react";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Link as LinkIcon, Code, Quote, Minus, Table as TableIcon, Eye, Pencil } from "lucide-react";
import MarkdownView from "@/components/MarkdownView";

/**
 * MarkdownEditor — lightweight markdown editor for admins.
 * - Toolbar buttons wrap the current selection in markdown syntax.
 * - Live Preview toggle.
 * - No external editor dependency — just a textarea + a renderer.
 */
const TOOLS = (insert) => [
  { icon: Heading1, title: "Heading 1", action: () => insert("# ", "", true) },
  { icon: Heading2, title: "Heading 2", action: () => insert("## ", "", true) },
  { icon: Bold,     title: "Bold",      action: () => insert("**", "**") },
  { icon: Italic,   title: "Italic",    action: () => insert("*", "*") },
  { icon: List,     title: "Bulleted list",  action: () => insert("- ", "", true) },
  { icon: ListOrdered, title: "Numbered list", action: () => insert("1. ", "", true) },
  { icon: Quote,    title: "Blockquote", action: () => insert("> ", "", true) },
  { icon: LinkIcon, title: "Link",       action: () => insert("[", "](https://)") },
  { icon: Code,     title: "Inline code", action: () => insert("`", "`") },
  { icon: TableIcon, title: "Insert table", action: () => insert("\n\n| Col 1 | Col 2 |\n|-------|-------|\n| Val A | Val B |\n\n") },
  { icon: Minus,    title: "Horizontal rule", action: () => insert("\n\n---\n\n") },
];

export default function MarkdownEditor({ value, onChange, minHeight = 500 }) {
  const taRef = useRef(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview" | "split"

  const insert = (before, after = "", lineStart = false) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = value || "";
    let prefix = text.slice(0, start);
    let middle = text.slice(start, end);
    let suffix = text.slice(end);

    if (lineStart) {
      // Move 'before' to the start of the line
      const nlIdx = prefix.lastIndexOf("\n");
      if (nlIdx >= 0 && prefix.length > nlIdx + 1) {
        prefix = prefix.slice(0, nlIdx + 1) + before + prefix.slice(nlIdx + 1);
      } else if (prefix.length === 0) {
        prefix = before + prefix;
      } else {
        // current line is the first line
        prefix = before + prefix;
      }
      const next = prefix + middle + after + suffix;
      onChange(next);
      setTimeout(() => {
        ta.focus();
        const pos = end + before.length;
        ta.setSelectionRange(pos, pos);
      }, 0);
      return;
    }

    const next = prefix + before + middle + after + suffix;
    onChange(next);
    setTimeout(() => {
      ta.focus();
      const selStart = start + before.length;
      const selEnd = end + before.length;
      ta.setSelectionRange(selStart, selEnd || selStart);
    }, 0);
  };

  return (
    <div className="rounded-2xl border border-[#E0E6E1] bg-white overflow-hidden" data-testid="md-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#E0E6E1] bg-[#F7FAF7]">
        <div className="flex flex-wrap items-center gap-0.5">
          {TOOLS(insert).map((t, i) => (
            <button
              key={i}
              type="button"
              onClick={t.action}
              title={t.title}
              className="w-8 h-8 rounded-md flex items-center justify-center text-[#1B291D] hover:bg-[#43A047]/12 hover:text-[#43A047] transition-colors"
              data-testid={`md-btn-${t.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <t.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-full bg-white border border-[#E0E6E1] p-0.5">
          <button
            type="button"
            onClick={() => setMode("edit")}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase transition-colors ${mode === "edit" ? "bg-[#1B291D] text-white" : "text-[#617364] hover:text-[#1B291D]"}`}
            data-testid="md-mode-edit"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("split")}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase transition-colors ${mode === "split" ? "bg-[#1B291D] text-white" : "text-[#617364] hover:text-[#1B291D]"}`}
            data-testid="md-mode-split"
          >
            Split
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase transition-colors ${mode === "preview" ? "bg-[#43A047] text-white" : "text-[#617364] hover:text-[#43A047]"}`}
            data-testid="md-mode-preview"
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={`grid ${mode === "split" ? "grid-cols-2 divide-x divide-[#E0E6E1]" : "grid-cols-1"}`} style={{ minHeight }}>
        {(mode === "edit" || mode === "split") && (
          <textarea
            ref={taRef}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="# Heading

Write your content in **Markdown**.

- Bullet
- Another bullet"
            className="w-full p-4 text-sm font-mono leading-relaxed text-[#1B291D] outline-none resize-none border-0 focus:ring-0 placeholder:text-[#617364]/50"
            style={{ minHeight }}
            spellCheck={false}
            data-testid="md-textarea"
          />
        )}
        {(mode === "preview" || mode === "split") && (
          <div className="p-5 overflow-auto bg-[#FCFDFC]" style={{ minHeight }} data-testid="md-preview">
            {value ? <MarkdownView source={value} /> : <p className="text-xs text-[#617364] italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
